import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FlaskConical } from "lucide-react";
import { FaSearch, FaSpinner, FaTimes, FaPlus, FaCheck } from "react-icons/fa";
import toast from "react-hot-toast";
import { store } from "../../../Store/store";
import * as HVTRegistrationService from "../../../Services/HVTRegistration/HVTRegistrationService";

const PRE_TEST_FIELDS = [
    "gender", "salutation", "firstName", "lastName", "mobile", "email",
    "age", "visitDate", "pincode", "locationId", "addressLine",
];

const TestsSection = () => {
    const userInfo = store.getState();
    const accountDetails = userInfo?.Partner_Account_Details;

    const { register, setValue, watch, trigger, formState: { errors } } = useFormContext();
    const gender = watch("gender");
    const orgId = watch("orgId");
    const tests = watch("tests") || [];

    const [testSearchText, setTestSearchText] = useState("");
    const [testSearchResults, setTestSearchResults] = useState([]);
    const [noTestsFound, setNoTestsFound] = useState(false);
    const [isSearchingTests, setIsSearchingTests] = useState(false);
    const [frequentTests, setFrequentTests] = useState([]);
    const [isLoadingFrequent, setIsLoadingFrequent] = useState(true);
    const [addingTestCode, setAddingTestCode] = useState(null);
    const [activeResultIndex, setActiveResultIndex] = useState(-1);

    const searchWrapRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        setActiveResultIndex(testSearchResults.length > 0 ? 0 : -1);
    }, [testSearchResults]);

    useEffect(() => {
        if (activeResultIndex < 0 || !dropdownRef.current) return;
        dropdownRef.current.children[activeResultIndex]?.scrollIntoView({ block: "nearest" });
    }, [activeResultIndex]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
                setTestSearchResults([]);
                setNoTestsFound(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setIsLoadingFrequent(true);
        const payload = {
            Gender: gender,
            // UserId: null,
            // PartnerCode: null
        }
        HVTRegistrationService.GetFrequentTestList(payload)
            .then(({ data }) => {
                setFrequentTests(data?.IsSuccess && data?.Data?.length ? data.Data : []);
            })
            .catch(() => setFrequentTests([]))
            .finally(() => setIsLoadingFrequent(false));
    }, []);

    const fetchTests = () => {
        setIsSearchingTests(true);
        setNoTestsFound(false);

        const payload = {
            clientCode: accountDetails?.MHL_Client_Code__c,
            testSearch: testSearchText,
            orgId: orgId,
            isExactMatch: false,
            Gender: gender,
        };

        HVTRegistrationService.UniversalSearchTestDetailByTestCode(payload)
            .then(({ data }) => {
                const results = data?.IsSuccess && data?.Data?.length ? data.Data : [];
                setTestSearchResults(results);
                setNoTestsFound(results.length === 0);
            })
            .catch(() => { setTestSearchResults([]); setNoTestsFound(true); })
            .finally(() => setIsSearchingTests(false));
    };

    const handleSearchTests = () => {
        if (!testSearchText.trim()) return;
        trigger(PRE_TEST_FIELDS).then((valid) => {
            if (!valid) {
                toast.error("Please complete patient and location details first.");
                return;
            }
            fetchTests();
        });
    };

    const handleTestSearchTextChange = (value) => {
        setTestSearchText(value.charAt(0).toUpperCase() + value.slice(1));
        setTestSearchResults([]);
        setNoTestsFound(false);
    };

    const handleSearchInputKeyDown = (e) => {
        if (testSearchResults.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveResultIndex((prev) => Math.min(prev + 1, testSearchResults.length - 1));
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveResultIndex((prev) => Math.max(prev - 1, 0));
                return;
            }
            if (e.key === "Enter") {
                e.preventDefault();
                const item = testSearchResults[activeResultIndex];
                if (item && !tests.some((t) => t.TEST_CODE === item.TestCode)) {
                    handleAddTest(item);
                }
                return;
            }
            if (e.key === "Escape") {
                setTestSearchResults([]);
                return;
            }
        }
        if (e.key === "Enter") {
            e.preventDefault();
            handleSearchTests();
        }
    };

    const handleAddTest = (item) => {
        if (tests.some((t) => t.TEST_CODE === item.TestCode)) return;
        const netAmount = parseFloat(item.NetMrp) || 0;
        if (netAmount <= 0) {
            toast.error(`${item.TestName} can't be added - price is not available for this location.`);
            return;
        }
        setValue("tests", [
            ...tests,
            {
                TEST_CODE: item.TestCode,
                TEST_NAME: item.TestName,
                TEST_AMOUNT: netAmount,
                TEST_DISCOUNT: 0,
            },
        ], { shouldValidate: true });
        setTestSearchText("");
        setTestSearchResults([]);
    };

    const handleRemoveTest = (code) => {
        setValue("tests", tests.filter((t) => t.TEST_CODE !== code), { shouldValidate: true });
    };

    const handleClearAllTests = () => {
        setValue("tests", [], { shouldValidate: true });
    };

    const handleAddFrequentTest = (item) => {
        if (tests.some((t) => t.TEST_CODE === item.TestCode) || addingTestCode) return;
        trigger(PRE_TEST_FIELDS).then((valid) => {
            if (!valid) {
                toast.error("Please complete patient and location details first.");
                return;
            }
            setAddingTestCode(item.TestCode);

            const payload = {
                clientCode: accountDetails?.MHL_Client_Code__c,
                testSearch: item.TestCode,
                orgId: orgId,
                isExactMatch: true,
                Gender: gender,
            };

            HVTRegistrationService.UniversalSearchTestDetailByTestCode(payload)
                .then(({ data }) => {
                    const results = data?.IsSuccess && data?.Data?.length ? data.Data : [];
                    const match = results.find((r) => r.TestCode === item.TestCode) || results[0];
                    if (!match) {
                        toast.error("Unable to fetch this test's details. Please try searching for it instead.");
                        return;
                    }
                    handleAddTest(match);
                })
                .catch(() => toast.error("Something went wrong while adding this test."))
                .finally(() => setAddingTestCode(null));
        });
    };

    return (
        <div className="hvt-quick-section">
            <div className="hvt-quick-section-title required"><FlaskConical size={13} /> Tests</div>

            <input type="hidden" {...register("tests", { validate: (v) => (v && v.length > 0) || "Please add at least one test" })} />
            {errors.tests && <small className="text-danger d-block mb-2">{errors.tests.message}</small>}

            {!isLoadingFrequent && frequentTests.length > 0 && testSearchResults.length === 0 && (
                <div className="mb-2">
                    <div className="hvt-frequent-tests-label">Frequently booked</div>
                    <div className="hvt-frequent-tests-row">
                        {frequentTests.map((item) => {
                            const isAdded = tests.some((t) => t.TEST_CODE === item.TestCode);
                            const isAdding = addingTestCode === item.TestCode;
                            return (
                                <button
                                    key={item.TestCode}
                                    type="button"
                                    tabIndex={-1}
                                    className={`hvt-test-pill hvt-test-pill-suggest ${isAdded ? "added" : ""}`}
                                    onClick={() => handleAddFrequentTest(item)}
                                    disabled={isAdded || isAdding}
                                >
                                    {item.TestName}
                                    {isAdding ? <FaSpinner className="fa-spin" size={10} /> : isAdded ? <FaCheck size={10} /> : <FaPlus size={10} />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div ref={searchWrapRef}>
                <div className="hvt-test-search-box">
                    <input
                        type="text"
                        className="hvt-test-search-input"
                        placeholder="Search test name or code"
                        value={testSearchText}
                        onChange={(e) => handleTestSearchTextChange(e.target.value)}
                        onKeyDown={handleSearchInputKeyDown}
                    />
                    <button type="button" tabIndex={-1} className="hvt-test-search-btn" onClick={handleSearchTests}>
                        {isSearchingTests ? <FaSpinner className="fa-spin" size={13} /> : <FaSearch size={13} />}
                    </button>
                </div>

                {testSearchResults.length > 0 && (
                    <>
                        {testSearchResults.length > 5 && (
                            <small className="hvt-test-dropdown-count">
                                {testSearchResults.length} results — refine your search to narrow it down
                            </small>
                        )}
                        <ul className="hvt-test-dropdown" ref={dropdownRef}>
                            {testSearchResults.map((item, idx) => {
                                const isAdded = tests.some((t) => t.TEST_CODE === item.TestCode);
                                return (
                                    <li
                                        key={item.TestCode}
                                        className={`hvt-test-dropdown-item ${isAdded ? "hvt-test-dropdown-item-added" : ""} ${idx === activeResultIndex ? "hvt-test-dropdown-item-active" : ""}`}
                                        onClick={() => !isAdded && handleAddTest(item)}
                                        onMouseEnter={() => setActiveResultIndex(idx)}
                                    >
                                        <div>
                                            <div className="hvt-test-dropdown-name">{item.TestName}</div>
                                            <span className="hvt-test-dropdown-code">{item.TestCode}</span>
                                        </div>
                                        <span className={`hvt-test-dropdown-add ${isAdded ? "hvt-test-dropdown-added" : ""}`}>
                                            {isAdded ? <FaCheck size={11} /> : <FaPlus size={11} />}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}

                {noTestsFound && !isSearchingTests && (
                    <div className="hvt-test-dropdown" style={{ padding: "10px 12px", fontSize: 13, color: "#6c757d" }}>
                        No tests found
                    </div>
                )}
            </div>

            {tests.length > 0 && testSearchResults.length === 0 && (
                <div className="hvt-added-tests">
                    <div className="hvt-added-tests-header">
                        <div className="hvt-frequent-tests-label mb-0">
                            Selected Tests <span className="hvt-added-tests-count">{tests.length}</span>
                        </div>
                        <button type="button" tabIndex={-1} className="hvt-added-tests-clear" onClick={handleClearAllTests}>Clear all</button>
                    </div>
                    <div className="hvt-added-tests-row">
                        {tests.map((t) => (
                            <div key={t.TEST_CODE} className="hvt-added-test-pill">
                                <span>{t.TEST_NAME}</span>
                                <button type="button" tabIndex={-1} onClick={() => handleRemoveTest(t.TEST_CODE)}><FaTimes size={11} /></button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TestsSection;
